<script setup>
// Astra DS · MenuIdioma — la lista de idiomas (04.11.26).
//
// Escritorio: `Pattern/MenuIdioma 2829:1008`. Móvil: `Pattern/MenuIdioma · móvil
// 2836:89`. DESIGN.md § La cabecera de 70 y el selector de idioma.
//
// Cuatro filas de 44 en el orden de `IDIOMAS`: Español · English · Català ·
// Français, cada uno nombrado en su lengua. Bandera, a 10 el nombre en
// `mono/label` y el código a la derecha en `mono/micro` `text/tertiary`.
//
//   escritorio  200 de ancho, 16 a los lados. El actual con una marca de 2 × 44
//               en `border/accent` pegada a la izquierda. Filete `border/strong`
//               a izquierda, derecha y abajo.
//   móvil       todo el ancho de la hoja, 20 a los lados. El actual con el
//               cuadrado de 7 × 7 en `action/primary` (el de sección activa del
//               menú); los demás guardan su hueco para alinear. Filete fuerte
//               abajo y `border/subtle` entre filas.
//
// 🔴 **No se coloca sola**: quien la abre la pone en su sitio (`SiteHeader` la
// cuelga del filete de la cabecera; `NavSheet`, de la cabecera del menú). Aquí
// solo va la lista y el teclado. Sin movimiento: aparece y desaparece de golpe.
//
// Con teclado: al abrir, el foco va al idioma actual; flechas arriba y abajo
// recorren; Intro o espacio eligen (son botones). Esc lo resuelve quien la abre,
// porque el foco tiene que volver a su selector.
import { ref, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import BanderaIdioma from "./BanderaIdioma.vue";
import { IDIOMAS, IDIOMA, NOMBRE_DEL_IDIOMA } from "@/i18n/idioma";

defineProps({
  movil: { type: Boolean, default: false },
  id: { type: String, default: "menu-idioma" },
});

const emit = defineEmits(["elegir"]);
const { t } = useI18n();

const filas = ref([]);

onMounted(async () => {
  await nextTick();
  filas.value[IDIOMAS.indexOf(IDIOMA)]?.focus();
});

function recorrer(e) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  e.preventDefault();
  const ahora = filas.value.indexOf(document.activeElement);
  const paso = e.key === "ArrowDown" ? 1 : -1;
  const siguiente = (ahora + paso + IDIOMAS.length) % IDIOMAS.length;
  filas.value[siguiente]?.focus();
}
</script>

<template>
  <ul
    :id="id"
    class="menu-idioma"
    :class="{ 'menu-idioma--movil': movil }"
    role="menu"
    :aria-label="t('marco.cabecera.idiomas')"
    @keydown="recorrer"
  >
    <li v-for="idioma in IDIOMAS" :key="idioma" role="none">
      <button
        :ref="(el) => (filas[IDIOMAS.indexOf(idioma)] = el)"
        type="button"
        class="menu-idioma__fila"
        :class="{ 'is-actual': idioma === IDIOMA }"
        role="menuitemradio"
        :aria-checked="idioma === IDIOMA ? 'true' : 'false'"
        :lang="idioma"
        @click="emit('elegir', idioma)"
      >
        <span class="menu-idioma__nombre">
          <i v-if="movil" class="menu-idioma__cuadro" aria-hidden="true" />
          <BanderaIdioma :idioma="idioma" />
          <span>{{ NOMBRE_DEL_IDIOMA[idioma] }}</span>
        </span>
        <span class="menu-idioma__codigo" aria-hidden="true">{{ idioma }}</span>
      </button>
    </li>
  </ul>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.menu-idioma {
  box-sizing: border-box;
  width: 200px;
  margin: 0;
  padding: 0;
  list-style: none;
  background: var(--color-bg-menu);
  border: 1px solid var(--color-border-strong);
  border-top: 0;
}

.menu-idioma__fila {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  margin: 0;
  padding: 0 16px;
  border: 0;
  border-radius: 0;
  background: none;
  color: var(--color-text-secondary);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }

  &.is-actual {
    color: var(--color-text-primary);
  }

  // La marca del actual en escritorio: 2 × 44 en `border/accent`, pegada a la
  // izquierda.
  &.is-actual::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 44px;
    background: var(--color-border-accent);
    content: "";
  }
}

.menu-idioma__nombre {
  @include t.mono-label;
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.menu-idioma__codigo {
  @include t.mono-micro;
  color: var(--color-text-tertiary);
}

// ── Móvil ──
.menu-idioma--movil {
  width: 100%;
  border: 0;
  border-bottom: 1px solid var(--color-border-strong);

  li + li {
    border-top: 1px solid var(--color-border-subtle);
  }

  .menu-idioma__fila {
    padding: 0 20px;

    &.is-actual::before {
      content: none;
    }
  }
}

// El cuadrado de 7 × 7: solo el actual lo pinta; los demás guardan el hueco.
.menu-idioma__cuadro {
  flex: none;
  width: 7px;
  height: 7px;
}

.is-actual .menu-idioma__cuadro {
  background: var(--color-action-primary-default);
}
</style>
